import './MessageHub.css';
import {
  AlertTriangle,
  FileText,
  Loader2,
  MessageSquare,
  Paperclip,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { getSocket, initSocket } from '../../utils/sockets';
import axiosInstance from '../../api/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadMessagingAttachment } from '../../api/uploads';
import { inferAttachmentMediaKind, isPdfAttachment } from '../../utils/messageAttachments';
import {
  resolveQuestionFromMessage,
  type QuestionMessagePayload,
} from '../../utils/questionMessage';
import { isFirstCandidateTaggedReply } from '../../utils/questionThreadMessages';
import { isNodeBackend, supportsDedicatedChatSocket } from '../../utils/backendConfig';
import { normalizeApiMessage } from '../../utils/normalizeApiMessage';
import {
  createPersistentChatSocket,
  formatWsMessage,
  sendChatMarkSeen,
  sendChatPayload,
  sendChatTyping,
  type ChatConnectionState,
  type PersistentChatSocket,
} from '../../utils/realtime';
import { API_ROUTES } from '../../config/api-routes';
import { useUserStore } from '../../store/userStore';
import { toast } from 'react-toastify';
import { QuestionSectionBlocks } from './QuestionSectionBlocks';
import { AttachmentView } from './AttachmentView';
import { HubAvatar } from './MessageHubShared';
import { MessageHubLoading } from './MessageHubLoading';
// import { useUserStore } from '../../store/userStore';

interface Message {
  id: string;
  senderId: number;
  content: string;
  createdAt: string;
  kind?: 'chat' | 'question' | string;
  question?: QuestionMessagePayload | null;
  status?: 'sent' | 'delivered' | 'seen' | string;
  attachments?: {
    id: string;
    fileName: string;
    fileUrl: string;
    type: string;
    mimeType: string;
  }[];
  sender: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
    profileImg: string | null;
    role: string;
  };
  pending?: boolean;
  chatRequestId?: string;
}
interface Conversation {
  id: string;
  uuid?: string;
  online?: boolean;
  title?: string | null;
  type: string;
  participants: {
    id: number;
    name: string;
    company?: string;
    profileImg?: string | null;
  }[];
  updatedAt: string;
  status?: 'active' | 'blocked' | 'muted' | 'removed';
}

interface MessagingFlowProps {
  conversation: Conversation | null;
  onBackToInbox?: () => void;
  onConversationDeleted?: () => void;
}

export default function MessagingFlow({
  conversation,
  onBackToInbox,
  onConversationDeleted,
}: MessagingFlowProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingConversation, setIsDeletingConversation] = useState(false);
  const user = useUserStore((state) => state.user);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatConnectionRef = useRef<PersistentChatSocket | null>(null);
  const [chatConnectionState, setChatConnectionState] =
    useState<ChatConnectionState>('disconnected');
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [isPeerOnline, setIsPeerOnline] = useState(false);

  const conversationUuid = conversation?.uuid || conversation?.id?.toString() || '';

  const sortMessages = (list: Message[]) =>
    [...list].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const upsertMessage = useCallback((incoming: Message) => {
    setMessages((prev) => {
      const withoutTemp = prev.filter(
        (m) => !String(m.id).startsWith('temp-') && !(m.pending && m.senderId === incoming.senderId)
      );
      if (withoutTemp.some((m) => m.id === incoming.id)) {
        return withoutTemp.map((m) => (m.id === incoming.id ? incoming : m));
      }
      return sortMessages([...withoutTemp, incoming]);
    });
  }, []);

  const loadMessages = useCallback(async () => {
    if (!conversation) return;
    setMessagesLoading(true);
    try {
      const url = API_ROUTES.MESSAGESHUB.GET_MESSAGES.replace(
        ':conversationId',
        conversation.id.toString()
      );
      const response = await axiosInstance.get(url);
      if (response.data?.success && Array.isArray(response.data.data)) {
        setMessages(sortMessages(response.data.data as Message[]));
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [conversation]);

  useEffect(() => {
    if (!user?.id) return;
    initSocket(user.id);
    const socket = getSocket();
    if (!socket) return;

    const onNewMessage = (notif: Record<string, unknown>) => {
      const convUuid = String(notif.conversationUuid || '');
      const convId = String(notif.conversationId || '');
      const activeUuid = conversationUuid;
      const activeId = String(conversation?.id || '');
      const matches =
        (convUuid && convUuid === activeUuid) ||
        (convId && (convId === activeId || convId === activeUuid));

      const embedded = notif.data as Record<string, unknown> | undefined;
      if (embedded?.id && matches) {
        upsertMessage(normalizeApiMessage(embedded) as Message);
        return;
      }

      if (!matches) return;
      void loadMessages();
    };

    socket.on('newMessage', onNewMessage);
    return () => {
      socket.off('newMessage', onNewMessage);
    };
  }, [user?.id, conversationUuid, conversation?.id, loadMessages, upsertMessage]);

  useEffect(() => {
    if (!conversationUuid || !user?.id) return;

    setIsOtherTyping(false);
    setIsPeerOnline(false);

    if (!supportsDedicatedChatSocket()) {
      return;
    }

    const connection = createPersistentChatSocket(conversationUuid, {
      onConnectionChange: (state) => {
        setChatConnectionState(state);
        if (state !== 'connected') {
          setIsOtherTyping(false);
        }
      },
      onOpen: () => {
        sendChatMarkSeen(chatConnectionRef.current?.getSocket() ?? null);
      },
      onMessage: (raw) => {
        const formatted = formatWsMessage(raw) as Message;
        upsertMessage(formatted);
      },
      onStatus: (messageId, status) => {
        setMessages((prev) =>
          prev.map((m) => (String(m.id) === String(messageId) ? { ...m, status } : m))
        );
      },
      onTyping: (userId, isTyping) => {
        if (userId !== user.id) setIsOtherTyping(isTyping);
      },
      onPresence: (userId, isOnline) => {
        if (userId !== user.id) {
          setIsPeerOnline(isOnline);
        }
      },
    });

    chatConnectionRef.current = connection;
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      connection?.disconnect();
      chatConnectionRef.current = null;
      setChatConnectionState('disconnected');
    };
  }, [conversationUuid, user?.id, upsertMessage]);

  const handleComposerChange = (value: string) => {
    setNewMessage(value);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    sendChatTyping(chatConnectionRef.current?.getSocket() ?? null, true);
    typingTimerRef.current = setTimeout(() => {
      sendChatTyping(chatConnectionRef.current?.getSocket() ?? null, false);
    }, 1200);
  };

  const renderMessageStatus = (status?: string) => {
    const normalized = status || 'sent';
    const seen = normalized === 'seen';
    const label = normalized === 'delivered' ? 'Delivered' : seen ? 'Read' : 'Sent';
    return (
      <span
        className={`mh-msg-status ${seen ? 'mh-msg-status--seen' : ''}`}
        aria-label={label}
        title={label}
      >
        {normalized === 'sent' ? '✓' : '✓✓'}
      </span>
    );
  };

  const renderAttachment = (
    file: NonNullable<Message['attachments']>[number],
    isSender: boolean
  ) => {
    const kind = inferAttachmentMediaKind(file);
    if (kind === 'image') {
      return (
        <a href={file.fileUrl} target="_blank" rel="noreferrer" className="block">
          <img
            src={file.fileUrl}
            alt={file.fileName || 'Attachment'}
            className="mh-bubble-attachment-img"
          />
        </a>
      );
    }
    if (kind === 'video') {
      return (
        <video
          controls
          playsInline
          preload="metadata"
          src={file.fileUrl}
          className="mh-bubble-attachment-video"
        >
          <track kind="captions" />
        </video>
      );
    }
    if (kind === 'audio') {
      return <audio controls src={file.fileUrl} className="mb-2 mt-1 w-full max-w-xs" />;
    }
    if (isPdfAttachment(file)) {
      return (
        <a
          href={file.fileUrl}
          target="_blank"
          rel="noreferrer"
          className="mh-bubble-attachment-doc"
        >
          <FileText className="mb-1 h-8 w-8 opacity-80" aria-hidden />
          <span className="text-xs font-medium">{file.fileName || 'PDF document'}</span>
          <span className="text-[10px] opacity-80">Tap to open</span>
        </a>
      );
    }
    return (
      <a href={file.fileUrl} target="_blank" rel="noreferrer" className="mh-bubble-attachment-link">
        {isSender ? 'Download attachment' : file.fileName || 'Download attachment'}
      </a>
    );
  };

  const renderMessageBody = (msg: Message, isSender: boolean) => {
    const question = resolveQuestionFromMessage(msg);
    if (question) {
      return (
        <QuestionSectionBlocks
          source={{
            section: question.section,
            questionText: question.questionText,
            questionItems: question.questionItems,
          }}
          badgeLabel="Q"
          variant={isSender ? 'outgoing' : 'incoming'}
        />
      );
    }
    if (msg.chatRequestId) {
      const isFirstAnswer = isSender && isFirstCandidateTaggedReply(msg, messages, user?.id);
      if (isFirstAnswer) {
        return (
          <>
            <span className="mh-bubble-tag">Your answer</span>
            {msg.content?.trim() ? <p className="mh-bubble-body">{msg.content}</p> : null}
          </>
        );
      }
      if (msg.kind === 'question') {
        const embedded = resolveQuestionFromMessage(msg);
        return (
          <QuestionSectionBlocks
            source={{
              section: embedded?.section || 'General',
              questionText: embedded?.questionText || msg.content,
              questionItems: embedded?.questionItems,
            }}
            badgeLabel="Q"
            variant={isSender ? 'outgoing' : 'incoming'}
          />
        );
      }
      if (msg.content?.trim()) {
        return <p className="mh-bubble-body">{msg.content}</p>;
      }
      return null;
    }
    if (msg.content?.trim()) {
      return <p className="mh-bubble-body">{msg.content}</p>;
    }
    return null;
  };

  const renderMessageMeta = (msg: Message, isSender: boolean) => (
    <>
      <span>
        {msg.pending
          ? 'Sending…'
          : new Date(msg.createdAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
      </span>
      {isSender && !msg.pending ? renderMessageStatus(msg.status || 'sent') : null}
    </>
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle sending message
  // Handle sending message
  const handleSendMessage = async () => {
    if (!conversation) return;

    const messageText = newMessage.trim();
    const filesToSend = [...selectedFiles];
    if (!messageText && filesToSend.length === 0) return;

    const tempId = `temp-${Date.now()}`;
    const firstFile = filesToSend[0];
    const optimisticAttachments =
      firstFile && user
        ? [
            {
              id: tempId,
              fileName: firstFile.name,
              fileUrl: URL.createObjectURL(firstFile),
              type: firstFile.type.startsWith('video/')
                ? 'video'
                : firstFile.type.startsWith('image/')
                  ? 'image'
                  : firstFile.type.startsWith('audio/')
                    ? 'audio'
                    : 'file',
              mimeType: firstFile.type,
            },
          ]
        : [];

    const optimisticMessage: Message = {
      id: tempId,
      senderId: user?.id ?? 0,
      content: messageText,
      createdAt: new Date().toISOString(),
      status: 'sent',
      attachments: optimisticAttachments,
      sender: {
        id: user?.id ?? 0,
        firstName: user?.firstName ?? null,
        lastName: user?.lastName ?? null,
        email: user?.email ?? '',
        profileImg: user?.profileImg ?? null,
        role: '',
      },
    };

    setMessages((prev) => sortMessages([...prev, optimisticMessage]));

    // Clear draft immediately so the UI feels responsive.
    setNewMessage('');
    setSelectedFiles([]);

    const failSend = (err: unknown) => {
      console.error('Error sending message:', err);
      toast.error(err instanceof Error ? err.message : 'Could not send message.');
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      setNewMessage(messageText);
      setSelectedFiles(filesToSend);
    };

    try {
      setIsSending(true);
      let attachmentUrl: string | undefined;
      let attachmentType: string | undefined;

      if (filesToSend.length > 0) {
        const file = filesToSend[0];
        const purpose = file.type.startsWith('image/')
          ? 'chat_image'
          : file.type.startsWith('audio/')
            ? 'chat_audio'
            : 'chat_file';
        const uploaded = await uploadMessagingAttachment(file, purpose);
        attachmentUrl = uploaded.publicUrl;
        attachmentType = uploaded.attachmentType;
      }

      const chatWs = chatConnectionRef.current?.getSocket() ?? null;
      const wsReady = chatWs?.readyState === WebSocket.OPEN;

      if (wsReady) {
        sendChatPayload(chatWs, {
          content: messageText,
          ...(attachmentUrl
            ? {
                attachment_url: attachmentUrl,
                attachment_type: attachmentType,
              }
            : {}),
        });
        sendChatTyping(chatWs, false);
        setIsSending(false);
        return;
      }

      setIsSending(false);

      void axiosInstance
        .post(API_ROUTES.MESSAGESHUB.SEND_MESSAGE(conversation.id.toString()), {
          messageText,
          content: messageText,
          attachment_url: attachmentUrl,
          attachment_type: attachmentType,
        })
        .then((res) => {
          if (res.data.success) {
            const saved = res.data.data as Record<string, unknown> | undefined;
            if (saved?.id) {
              upsertMessage(normalizeApiMessage(saved) as Message);
            } else if (!isNodeBackend()) {
              void loadMessages();
            }
            return;
          }
          setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
          setNewMessage(messageText);
          setSelectedFiles(filesToSend);
        })
        .catch(failSend);
    } catch (err) {
      failSend(err);
      setIsSending(false);
    }
  };

  // useEffect(() => {
  //   const chatContainer = document.querySelector('.chat-container');
  //   if (chatContainer) {
  //     chatContainer.scrollTop = chatContainer.scrollHeight;
  //   }
  // }, [messages]);

  useEffect(() => {
    if (!conversation?.id) {
      setMessages([]);
      setMessagesLoading(false);
      return;
    }
    setMessages([]);
    void loadMessages();
  }, [conversation?.id, loadMessages]);

  // Auto-scroll after messages finish loading
  useEffect(() => {
    if (messagesLoading) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    sendChatMarkSeen(chatConnectionRef.current?.getSocket() ?? null);
  }, [messages, messagesLoading]);

  const getOtherParticipant = () => {
    if (!conversation) return null;
    const participants = Array.isArray(conversation.participants) ? conversation.participants : [];
    return participants.find((p) => Number(p.id) !== Number(user?.id)) || participants[0] || null;
  };

  const otherParticipant = getOtherParticipant();
  const threadName = otherParticipant?.name || 'Unknown';

  const handleDeleteConversation = async () => {
    if (!conversation?.id) return;
    setIsDeletingConversation(true);

    try {
      await axiosInstance.delete(API_ROUTES.MESSAGESHUB.DELETE_CONVERSATION(conversation.id));
      setMessages([]);
      setShowDeleteModal(false);
      onConversationDeleted?.();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    } finally {
      setIsDeletingConversation(false);
    }
  };

  // ⛔ When no conversation is selected
  if (!conversation) {
    return (
      <div className="mh-empty-state">
        <div className="mh-empty-state-icon">
          <MessageSquare size={28} strokeWidth={1.5} />
        </div>
        <p className="text-sm font-medium text-slate-700">Select a conversation</p>
        <p className="mt-1 text-xs text-slate-500">
          Choose someone from your inbox to start chatting
        </p>
      </div>
    );
  }

  return (
    <div className="mh-thread-panel">
      <div className="mh-thread-panel-inner">
        {onBackToInbox ? (
          <div className="mh-thread-header mh-thread-header--mobile-only">
            <button type="button" className="mh-mobile-back" onClick={onBackToInbox}>
              ← Inbox
            </button>
          </div>
        ) : null}

        <header className="mh-thread-header">
          <HubAvatar name={threadName} large imageUrl={otherParticipant?.profileImg || null} />
          <div className="mh-thread-header-info">
            <p className="mh-thread-header-name">{threadName}</p>
            {isOtherTyping ? (
              <p className="mh-thread-header-sub mh-thread-header-typing">Typing…</p>
            ) : otherParticipant?.company ? (
              <p className="mh-thread-header-sub">{otherParticipant.company}</p>
            ) : null}
          </div>
          <span
            className={`mh-presence-pill ${
              (chatConnectionState === 'reconnecting' || chatConnectionState === 'connecting') &&
              !isPeerOnline
                ? 'mh-presence-pill--offline'
                : isPeerOnline
                  ? ''
                  : 'mh-presence-pill--offline'
            }`}
            title={
              (chatConnectionState === 'reconnecting' || chatConnectionState === 'connecting') &&
              !isPeerOnline
                ? 'Reconnecting your chat connection'
                : isPeerOnline
                  ? 'Currently online in this chat'
                  : 'Not in this chat right now'
            }
          >
            <span
              className={`mh-presence-dot ${
                !(
                  (chatConnectionState === 'reconnecting' ||
                    chatConnectionState === 'connecting') &&
                  !isPeerOnline
                ) && isPeerOnline
                  ? ''
                  : 'mh-presence-dot--offline'
              }`}
            />
            {(chatConnectionState === 'reconnecting' || chatConnectionState === 'connecting') &&
            !isPeerOnline
              ? 'Reconnecting…'
              : isPeerOnline
                ? 'Online'
                : 'Offline'}
          </span>
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="mh-thread-delete"
            title="Delete conversation"
            aria-label="Delete conversation"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </header>

        <div
          className={`mh-thread-messages chat-container${messagesLoading ? ' mh-thread-messages--loading' : ''}`}
        >
          {messagesLoading ? (
            <MessageHubLoading label="Messages" />
          ) : (
            <>
              {messages.map((msg) => {
                const isSender = msg.sender?.id === user?.id;
                const question = resolveQuestionFromMessage(msg);
                const hasAttachments = Boolean(msg.attachments && msg.attachments.length > 0);

                if (question && !hasAttachments) {
                  const hasAnswerAttachments = Boolean(
                    question.answerAttachments && question.answerAttachments.length > 0
                  );
                  return (
                    <div key={msg.id} className="mh-question-message-block">
                      {renderMessageBody(msg, isSender)}
                      {question.answerText || hasAnswerAttachments ? (
                        <div className={`mh-bubble-row ${isSender ? 'mh-bubble-row--mine' : ''}`}>
                          <div className={`mh-bubble ${isSender ? 'mh-bubble--mine' : ''}`}>
                            <span className="mh-bubble-tag">Answer</span>
                            {question.answerAttachments?.map((file, index) => (
                              <div key={`${file.fileUrl}-${index}`} className="mb-2">
                                <AttachmentView file={file} />
                              </div>
                            ))}
                            {question.answerText ? (
                              <p className="mh-bubble-body">{question.answerText}</p>
                            ) : null}
                            <div className="mh-bubble-meta">{renderMessageMeta(msg, isSender)}</div>
                          </div>
                        </div>
                      ) : (
                        <div className={`mh-bubble-row ${isSender ? 'mh-bubble-row--mine' : ''}`}>
                          <div className="mh-bubble-meta mh-bubble-meta--standalone">
                            {renderMessageMeta(msg, isSender)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div
                    key={msg.id}
                    className={`mh-bubble-row ${isSender ? 'mh-bubble-row--mine' : ''}`}
                  >
                    <div className={`mh-bubble ${isSender ? 'mh-bubble--mine' : ''}`}>
                      {hasAttachments ? (
                        <div className="mb-2 space-y-2">
                          {msg.attachments!.map((file) => (
                            <div key={file.id}>{renderAttachment(file, isSender)}</div>
                          ))}
                        </div>
                      ) : null}

                      {renderMessageBody(msg, isSender)}

                      <div className="mh-bubble-meta">{renderMessageMeta(msg, isSender)}</div>
                    </div>
                  </div>
                );
              })}

              {isOtherTyping ? (
                <div className="mh-bubble-row">
                  <div className="mh-typing-bubble" aria-label="Typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              ) : null}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {selectedFiles.length > 0 ? (
          <div className="mh-attachments-strip mh-thread-composer-bar">
            {selectedFiles.map((file, index) => {
              const type = file.type;
              const isImage = type.startsWith('image/');

              return (
                <div key={index} className="mh-attachment-chip">
                  {isImage ? (
                    <img src={URL.createObjectURL(file)} alt={file.name} />
                  ) : (
                    <FileText size={28} className="my-2 text-slate-500" />
                  )}
                  <span className="truncate w-full text-center">{file.name}</span>
                  <button
                    type="button"
                    className="absolute top-0.5 right-0.5 rounded bg-white/90 p-0.5 text-slate-600"
                    aria-label="Remove attachment"
                    onClick={() => removeFile(index)}
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        <div className="mh-composer mh-thread-composer-bar">
          <label htmlFor="mh-attachment" className="mh-composer-attach">
            <Paperclip className="h-5 w-5" aria-hidden />
            <input
              title="Attach file"
              id="mh-attachment"
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
          </label>

          <input
            className="mh-composer-input"
            placeholder="Type a message…"
            value={newMessage}
            onChange={(e) => handleComposerChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={isSending}
          />

          <button
            type="button"
            className="mh-composer-send"
            onClick={handleSendMessage}
            disabled={isSending || (!newMessage.trim() && selectedFiles.length === 0)}
          >
            <Send className="inline h-4 w-4" aria-hidden />
            {isSending ? '…' : 'Send'}
          </button>
        </div>
      </div>

      {showDeleteModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                  <AlertTriangle size={18} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">Delete Conversation</h3>
              </div>
              <button
                onClick={() => !isDeletingConversation && setShowDeleteModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
                aria-label="Close delete confirmation"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-5 py-4">
              <p className="text-sm text-gray-600">
                This will permanently delete this conversation for both users.
              </p>
              <p className="text-xs text-gray-400 mt-1">This action cannot be undone.</p>
            </div>

            <div className="px-5 py-4 bg-gray-50 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeletingConversation}
                className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-700 hover:bg-white disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConversation}
                disabled={isDeletingConversation}
                className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 disabled:bg-red-400 disabled:text-white disabled:opacity-80 inline-flex items-center gap-2 shadow-sm"
              >
                {isDeletingConversation && <Loader2 size={14} className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
